export const DIAGRAM_NODE_CAP = 6;

export type SkillNameMap = Record<string, string>;

export type DiagramNode = { id: string; label: string };
export type DiagramEdge = { from: string; to: string };
export type DiagramGraph = { nodes: DiagramNode[]; edges: DiagramEdge[] };

type StepLike = { id?: string; skill_id?: string };
type EdgeLike = { id?: string; from?: string; to?: string };

export type PlaybookDiagramSource = {
  title?: string;
  composition?: {
    nodes?: StepLike[];
    edges?: EdgeLike[];
    pipeline_steps?: StepLike[];
  };
  pipeline_steps?: StepLike[];
};

export type SkillDiagramSource = {
  name?: string;
  inputs?: Array<{ name?: string }>;
  outputs?: Array<{ name?: string }>;
};

export function sanitizeLabel(raw: string): string {
  const cleaned = raw
    .replace(/[\r\n"'`[\](){}<>#;]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  if (!cleaned) return 'Node';
  return cleaned.length > 28 ? `${cleaned.slice(0, 27)}…` : cleaned;
}

export function humanizeSkillId(id: string): string {
  const parts = id
    .replace(/^skill-/, '')
    .replace(/[_/]+/g, '-')
    .split('-')
    .filter(Boolean);
  if (parts.length === 0) return 'Skill';
  return parts.map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
}

function skillLabel(skillId: string, names?: SkillNameMap): string {
  const named = names?.[skillId]?.trim();
  if (named) return sanitizeLabel(named);
  return sanitizeLabel(humanizeSkillId(skillId));
}

export function capGraph(graph: DiagramGraph, cap = DIAGRAM_NODE_CAP): DiagramGraph {
  if (graph.nodes.length <= cap) return graph;
  const kept = graph.nodes.slice(0, cap);
  const keptIds = new Set(kept.map((n) => n.id));
  const overflow = graph.nodes.length - cap;
  const moreId = 'more';
  const keptEdges = graph.edges.filter((e) => keptIds.has(e.from) && keptIds.has(e.to));
  const last = kept[kept.length - 1];
  return {
    nodes: [...kept, { id: moreId, label: `+${overflow}` }],
    edges: [...keptEdges, { from: last.id, to: moreId }],
  };
}

function mermaidId(id: string): string {
  let next = id.replace(/[^A-Za-z0-9_]/g, '_');
  if (!next || !/^[A-Za-z]/.test(next)) next = `n_${next}`;
  return next;
}

export function toMermaid(graph: DiagramGraph): string {
  const lines = ['flowchart LR'];
  const seen = new Set<string>();
  const labels = new Map(graph.nodes.map((n) => [n.id, n.label]));
  const ref = (id: string) => {
    const mid = mermaidId(id);
    if (seen.has(id)) return mid;
    seen.add(id);
    return `${mid}[${labels.get(id) || sanitizeLabel(id)}]`;
  };
  if (graph.edges.length === 0) {
    for (const n of graph.nodes) lines.push(`  ${ref(n.id)}`);
    return lines.join('\n');
  }
  for (const e of graph.edges) {
    lines.push(`  ${ref(e.from)} --> ${ref(e.to)}`);
  }
  for (const n of graph.nodes) {
    if (!seen.has(n.id)) lines.push(`  ${ref(n.id)}`);
  }
  return lines.join('\n');
}

function playbookGraph(source: PlaybookDiagramSource, names?: SkillNameMap): DiagramGraph {
  const nodesIn = source.composition?.nodes ?? [];
  const edgesIn = source.composition?.edges ?? [];
  const steps = source.composition?.pipeline_steps ?? source.pipeline_steps ?? nodesIn;

  const skillOf = new Map<string, string>();
  for (const n of nodesIn) {
    if (n.id && n.skill_id) skillOf.set(n.id, n.skill_id);
  }
  for (const s of steps) {
    if (s.id && s.skill_id) skillOf.set(s.id, s.skill_id);
  }

  const order: string[] = [];
  const remember = (id?: string) => {
    if (!id || order.includes(id)) return;
    order.push(id);
  };
  for (const s of steps) remember(s.id);
  for (const n of nodesIn) remember(n.id);
  for (const e of edgesIn) {
    remember(e.from);
    remember(e.to);
  }

  const labelFor = (id: string) => {
    const skillId = skillOf.get(id);
    if (skillId) return skillLabel(skillId, names);
    return sanitizeLabel(humanizeSkillId(id));
  };

  if (edgesIn.length > 0 && order.length > 0) {
    return {
      nodes: order.map((id) => ({ id, label: labelFor(id) })),
      edges: edgesIn
        .filter((e) => e.from && e.to)
        .map((e) => ({ from: e.from as string, to: e.to as string })),
    };
  }

  if (steps.length > 0) {
    const nodes = steps
      .map((s, i) => {
        const id = s.id || `n${i}`;
        const skillId = s.skill_id || id;
        return { id, label: skillLabel(skillId, names) };
      })
      .filter((n, i, all) => all.findIndex((x) => x.id === n.id) === i);
    const edges = nodes.slice(1).map((n, i) => ({ from: nodes[i].id, to: n.id }));
    return { nodes, edges };
  }

  const title = sanitizeLabel((source.title || '').trim() || 'Playbook');
  return { nodes: [{ id: 'n0', label: title }], edges: [] };
}

export function playbookDiagram(source: PlaybookDiagramSource, names?: SkillNameMap): string {
  return toMermaid(capGraph(playbookGraph(source, names)));
}

function skillGraph(source: SkillDiagramSource): DiagramGraph {
  const name = sanitizeLabel((source.name || '').trim() || 'Skill');
  const skill = { id: 's', label: name };
  const inputs = (source.inputs ?? []).map((f, i) => ({
    id: `i${i}`,
    label: sanitizeLabel((f.name || '').trim() || `in ${i + 1}`),
    dir: 'in' as const,
  }));
  const outputs = (source.outputs ?? []).map((f, i) => ({
    id: `o${i}`,
    label: sanitizeLabel((f.name || '').trim() || `out ${i + 1}`),
    dir: 'out' as const,
  }));
  const ports = [...inputs, ...outputs];
  if (ports.length === 0) return { nodes: [skill], edges: [] };

  const portBudget = DIAGRAM_NODE_CAP - 1;
  let used = ports;
  let overflow = 0;
  if (ports.length > portBudget) {
    overflow = ports.length - (portBudget - 1);
    used = ports.slice(0, portBudget - 1);
  }

  const nodes: DiagramNode[] = [skill, ...used.map(({ id, label }) => ({ id, label }))];
  const edges: DiagramEdge[] = [];
  for (const p of used) {
    if (p.dir === 'in') edges.push({ from: p.id, to: 's' });
    else edges.push({ from: 's', to: p.id });
  }
  if (overflow > 0) {
    nodes.push({ id: 'more', label: `+${overflow}` });
    edges.push({ from: 's', to: 'more' });
  }
  return { nodes, edges };
}

export function skillDiagram(source: SkillDiagramSource): string {
  return toMermaid(skillGraph(source));
}

export function skillPortCounts(source: SkillDiagramSource): { inputs: number; outputs: number } {
  return {
    inputs: source.inputs?.length ?? 0,
    outputs: source.outputs?.length ?? 0,
  };
}

export const GLYPH_DOT_CAP = 4;
